/**
 * ScenarioPlanningPage.tsx
 * FEAT-2026-0007 Phase 6B.2 — Scenario Planning wizard shell.
 * Updated Phase 6D.4 — deep-link resume from ?source=&sourceKey= URL params.
 * Updated Phase 6F.5 — scenario dashboard ("Back to Dashboard" clears in-page;
 *   ?scenarioKey= opens a specific scenario directly; no-params entry shows list).
 *
 * Provides:
 *  - Phase step state: phase 1 (Machine planning) → phase 2 (Index tests) →
 *    phase 3 (Employee planning) — reordered by Index Tests Item 9.1 (R1)
 *  - Active scenario state: Scenario | null
 *  - Header chrome (ScenarioHeaderChrome) when a scenario is active
 *  - Deep-link auto-resolve:
 *      ?source=Quote|WorkOrder&sourceKey=UUID  → create-or-load draft + generate
 *      ?scenarioKey=KEY                        → load scenario directly by key
 *  - ScenarioDashboard (list of existing plans + create-new) when no active scenario
 *    and no URL params → replaces the bare ScenarioSourcePicker (6F.5)
 *  - Phase stepper + phase panels
 *  - Compare to Baseline toggle
 *
 * Back to Dashboard (6F.5): clears the active scenario and returns to the in-page
 * dashboard without navigating away from /schedule/scenario-planning.
 *
 * Deep-link guard:
 *  - resolvedRef.current ensures the auto-resolve fires exactly ONCE per unique
 *    (source, sourceKey) or (scenarioKey) pair.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { Scenario } from '../../../services/scheduleScenario';
import type { ScenarioSourceType } from '../../../services/scheduleScenario';
import { getScenario } from '../../../services/scheduleScenario';
import { readScheduleReturnTo } from '../../../utils/scheduleReturnTo';
import { ScenarioHeaderChrome } from './scenario/ScenarioHeaderChrome';
import { ScenarioPhaseStepper } from './scenario/ScenarioPhaseStepper';
import { ScenarioDashboard } from './scenario/ScenarioDashboard';
import { resolveScenarioForSource } from './scenario/useResolveScenarioForSource';
import { LockedPlanDialog } from './scenario/LockedPlanDialog';
import { useLockedPlanChoice } from './scenario/useLockedPlanChoice';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when a string is a valid ScenarioSourceType. */
function isValidSourceType(val: string | null): val is ScenarioSourceType {
  return val === 'Quote' || val === 'WorkOrder';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScenarioPlanningPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ── Return-to origin (deep-link back-nav fix, 2026-07-11) ─────────────────
  // The quote editor / work-order page pass { scheduleReturnTo } as navigation
  // state. Captured into a ref on first render because the ?source=→?scenarioKey
  // rewrites below use setSearchParams({replace:true}), which drops location.state
  // — the component stays mounted, so the ref keeps the origin alive.
  const returnToRef = useRef(readScheduleReturnTo(location.state));
  const returnTo = returnToRef.current;

  // ── URL persistence (Gigi, 2026-06-20) ─────────────────────────────────────
  // Keep the active scenario + phase in the URL (?scenarioKey=…&phase=…) so a
  // refresh resumes exactly where the planner was instead of dropping back to the
  // dashboard. Use replace so the wizard doesn't pollute browser history.
  const setActiveUrl = useCallback(
    (scenarioKey: string, ph: 1 | 2 | 3) => {
      setSearchParams({ scenarioKey, phase: String(ph) }, { replace: true });
    },
    [setSearchParams],
  );
  const clearUrl = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // ── State ──────────────────────────────────────────────────────────────────
  /** The active scenario being planned. Null = show the dashboard. */
  const [scenario, setScenario] = useState<Scenario | null>(null);

  /**
   * Current wizard phase: 1 = Machine planning, 2 = Index tests, 3 = Employee
   * planning. Reordered by Index Tests Item 9.1 (R1 — owner revision
   * 2026-07-23); old deep-links built against the pre-9.1 numbering
   * (`?phase=3` meaning Index) are an accepted casualty.
   */
  const [phase, setPhase] = useState<1 | 2 | 3>(1);

  /**
   * Compare-to-Baseline: the header toggle was REMOVED (2026-07-26 item 3 —
   * unused in practice). The phase panels' overlay plumbing stays dormant on a
   * constant false; deep-link resets keep the setter no-op'd below.
   */
  const compareToBaseline = false;
  const setCompareToBaseline = (_v: boolean) => { /* toggle removed 2026-07-26 */ };

  // ── Deep-link resolve state ────────────────────────────────────────────────
  /** True while the deep-link auto-resolve is in progress. */
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  /** Error message from the deep-link resolve, or null when none. */
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  /** Snackbar for success/info messages from the auto-resolve. */
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'error';
  }>({ open: false, message: '', severity: 'info' });

  /**
   * Ref guard: records the resolved URL params so the effect runs at most once per
   * unique set.  A ref (not state) is intentional: updating it must not trigger
   * another render cycle.
   */
  const resolvedRef = useRef<string | null>(null);

  /**
   * HOW the active scenario was opened (deep-link back-nav fix, 2026-07-11):
   *  - true  → opened via URL params (?scenarioKey= / ?source=&sourceKey=),
   *            i.e. the user arrived from OUTSIDE this page (quote editor,
   *            work-order page, pasted link, refresh);
   *  - false → opened from the in-page dashboard (open / create / duplicate).
   * Drives the browser-Back guard below: deep-linked opens must NOT hijack
   * Back — it should return to the page the user came from.
   */
  const openedViaDeepLinkRef = useRef(false);

  // ── Locked-plan choice (2026-07-10 silent-duplicate fix) ──────────────────
  // When the deep-linked source's only plan is Submitted, the resolve returns
  // { kind: 'locked' } instead of silently creating a duplicate draft. The
  // deep-link is how the quote editor's "Edit Schedule" and the WO pages'
  // "Add to Schedule" arrive here, so THIS dialog is what those users see:
  // Open plan (read-only) / Reopen to Edit / Create new draft. Cancelling
  // falls back to the dashboard, where the plan is listed.
  const lockedChoice = useLockedPlanChoice({
    onScenarioReady: (readyScenario, message, severity) => {
      // The locked-plan dialog is only reachable from a deep-link resolve.
      openedViaDeepLinkRef.current = true;
      setScenario(readyScenario);
      setPhase(1);
      setDeepLinkError(null);
      resolvedRef.current = `scenarioKey:${readyScenario.ScenarioKey}`;
      setActiveUrl(readyScenario.ScenarioKey, 1);
      setSnack({ open: true, message, severity });
    },
    onError: (message) => setSnack({ open: true, message, severity: 'error' }),
    onDismiss: () => {
      // Back to the dashboard; reset the guard so re-entry via URL re-resolves.
      resolvedRef.current = null;
      clearUrl();
    },
  });

  // ── Deep-link auto-resolve ─────────────────────────────────────────────────
  useEffect(() => {
    const rawSource    = searchParams.get('source');
    const sourceKey    = searchParams.get('sourceKey');
    const scenarioKey  = searchParams.get('scenarioKey');
    // Resume the wizard phase from the URL (defaults to phase 1).
    const phaseParam = searchParams.get('phase');
    const initialPhase: 1 | 2 | 3 = phaseParam === '3' ? 3 : phaseParam === '2' ? 2 : 1;

    // Nothing to do if no params or scenario already active
    if (scenario !== null) return;

    // ── Case 1: ?scenarioKey= — open a specific scenario by key ────────────
    if (scenarioKey) {
      const pairKey = `scenarioKey:${scenarioKey}`;
      if (resolvedRef.current === pairKey) return;
      resolvedRef.current = pairKey;
      openedViaDeepLinkRef.current = true;

      setDeepLinkLoading(true);
      setDeepLinkError(null);

      getScenario(scenarioKey)
        .then((loaded) => {
          setScenario(loaded);
          setPhase(initialPhase);
          setSnack({ open: true, message: `Opened ${loaded.Code} — ${loaded.Name}`, severity: 'info' });
        })
        .catch(() => {
          setDeepLinkError('Could not load the requested scenario. Please select one from the list.');
        })
        .finally(() => setDeepLinkLoading(false));

      return;
    }

    // ── Case 2: ?source=&sourceKey= — create-or-load draft ─────────────────
    if (!isValidSourceType(rawSource) || !sourceKey) return;

    const pairKey = `${rawSource}:${sourceKey}`;
    if (resolvedRef.current === pairKey) return;
    resolvedRef.current = pairKey;
    openedViaDeepLinkRef.current = true;

    setDeepLinkLoading(true);
    setDeepLinkError(null);

    resolveScenarioForSource(rawSource, sourceKey)
      .then((result) => {
        if (result.kind === 'locked') {
          // The source's only plan is Submitted — never silently create a
          // duplicate. Ask the user: Open / Reopen to Edit / Create new draft.
          lockedChoice.show(result.scenario, rawSource, sourceKey);
          return;
        }
        setScenario(result.scenario);
        setPhase(initialPhase);
        // Switch the URL to the resolved scenarioKey so a refresh resumes via the
        // scenario directly (cleaner than re-resolving the source each time).
        resolvedRef.current = `scenarioKey:${result.scenario.ScenarioKey}`;
        setActiveUrl(result.scenario.ScenarioKey, initialPhase);
        setSnack({ open: true, message: result.message, severity: result.severity });
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error
            ? err.message
            : 'Could not load scenario. Please select one from the list.';
        setDeepLinkError(msg);
        // Reset the guard so the user can retry via the dashboard
        resolvedRef.current = null;
      })
      .finally(() => setDeepLinkLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // searchParams stable per react-router; scenario excluded intentionally (guard above).
  }, [searchParams]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  /**
   * "Back to Dashboard" — clears the active scenario and returns to the in-page
   * dashboard (does NOT navigate away from /schedule/scenario-planning).
   * The deep-link guard is reset so re-entry with URL params works correctly.
   */
  const handleBackToDashboard = () => {
    setScenario(null);
    setPhase(1);
    setCompareToBaseline(false);
    setDeepLinkError(null);
    resolvedRef.current = null;
    clearUrl();
  };

  // Browser Back while a DASHBOARD-OPENED scenario is active should return to the
  // in-page dashboard (mirroring the "Back to Dashboard" button), not leave the
  // page to wherever the planner came from (e.g. the Machine Schedule). We push a
  // guard history entry when such a scenario becomes active and intercept the
  // resulting popstate. Re-armed only when the active scenario KEY changes (not on
  // every status update).
  //
  // Deep-link exemption (back-nav fix, 2026-07-11): scenarios opened via URL
  // params (?source=&sourceKey= from a quote's / work order's Add-to-Schedule
  // button, ?scenarioKey= from a pasted link or refresh) get NO guard entry and
  // NO popstate interception. Those users came from another page, so browser
  // Back must return there — the ?source→?scenarioKey rewrite uses
  // { replace: true }, leaving history as [origin, here] and Back working
  // naturally. Hijacking it trapped quote/WO users on this page.
  useEffect(() => {
    if (!scenario || openedViaDeepLinkRef.current) return;
    window.history.pushState(null, '');
    const onPop = () => handleBackToDashboard();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.ScenarioKey]);

  const handleScenarioUpdated = (updated: Scenario) => {
    setScenario(updated);
    // ScenarioKey is unchanged on a status update, so the URL stays correct.
  };

  /**
   * After delete — back to dashboard (same page, no navigate).
   */
  const handleScenarioDeleted = () => {
    setScenario(null);
    setPhase(1);
    setCompareToBaseline(false);
    setDeepLinkError(null);
    resolvedRef.current = null;
    clearUrl();
  };

  /** Wrap setPhase so the wizard phase is mirrored into the URL (refresh-safe). */
  const handlePhaseChange = useCallback(
    (ph: 1 | 2 | 3) => {
      setPhase(ph);
      if (scenario) setActiveUrl(scenario.ScenarioKey, ph);
    },
    [scenario, setActiveUrl],
  );

  /**
   * After duplicate — switch active scenario to the new copy, reset to Phase 1.
   */
  const handleDuplicated = (newScenario: Scenario) => {
    setScenario(newScenario);
    setPhase(1);
    setDeepLinkError(null);
    resolvedRef.current = `scenarioKey:${newScenario.ScenarioKey}`;
    // The copy was opened by an in-page action, not a deep-link — arm the
    // in-page Back-to-dashboard guard for it.
    openedViaDeepLinkRef.current = false;
    setActiveUrl(newScenario.ScenarioKey, 1);
  };

  /**
   * Called from ScenarioDashboard when the user opens or creates a scenario.
   */
  const handleOpenScenario = (opened: Scenario) => {
    setScenario(opened);
    setPhase(1);
    setCompareToBaseline(false);
    setDeepLinkError(null);
    resolvedRef.current = `scenarioKey:${opened.ScenarioKey}`;
    // Opened (or just created) from the in-page dashboard — browser Back should
    // return to that dashboard, so arm the guard.
    openedViaDeepLinkRef.current = false;
    setActiveUrl(opened.ScenarioKey, 1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Page header (when no active scenario and not loading) ─────────── */}
      {!scenario && !deepLinkLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <InsightsIcon sx={{ color: 'primary.main', fontSize: '1.6rem' }} />
          <Typography variant="h5" fontWeight={600} color="text.primary">
            Scenario Planning
          </Typography>
          {/* Deep-link origin breadcrumb — subtle path back to the quote / work
              order the user came from, even from the dashboard view. */}
          {returnTo && (
            <>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(returnTo.path)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Back to {returnTo.label}
              </Button>
            </>
          )}
        </Box>
      )}

      {/* ── Header chrome (when scenario is active) ────────────────────────── */}
      {scenario && (
        <ScenarioHeaderChrome
          scenario={scenario}
          onScenarioUpdated={handleScenarioUpdated}
          onScenarioDeleted={handleScenarioDeleted}
          onBackToDashboard={handleBackToDashboard}
          onDuplicated={handleDuplicated}
          returnTo={returnTo}
        />
      )}

      {/* ── Deep-link loading state ────────────────────────────────────────── */}
      {deepLinkLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 8,
          }}
        >
          <CircularProgress size={36} />
          <Typography variant="body1" color="text.secondary">
            Loading scenario…
          </Typography>
        </Box>
      )}

      {/* ── Deep-link error fallback ───────────────────────────────────────── */}
      {deepLinkError && !deepLinkLoading && !scenario && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {deepLinkError}. You can select a scenario from the list below.
          </Alert>
        </Box>
      )}

      {/* ── Main content (shown when not deep-link loading) ───────────────── */}
      {!deepLinkLoading && (
        !scenario ? (
          /* ── Dashboard: list existing + create-new ──────────────────────── */
          <ScenarioDashboard onOpenScenario={handleOpenScenario} />
        ) : (
          /* ── Wizard: stepper + phase panels ──────────────────────────────── */
          <ScenarioPhaseStepper
            phase={phase}
            onPhaseChange={handlePhaseChange}
            compareToBaseline={compareToBaseline}
            scenarioActive={scenario !== null}
            scenario={scenario}
          />
        )
      )}

      {/* ── Locked-plan choice (deep-link met a Submitted plan) ───────────── */}
      <LockedPlanDialog
        open={lockedChoice.dialogOpen}
        scenario={lockedChoice.lockedScenario}
        busy={lockedChoice.busy}
        onOpenPlan={lockedChoice.handleOpenPlan}
        onReopen={lockedChoice.handleReopen}
        onCreateNew={lockedChoice.handleCreateNew}
        onClose={lockedChoice.handleClose}
      />

      {/* ── Snackbar for deep-link auto-resolve messages ──────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
